import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import ContactPopup from './ContactPopup';

const Navbar = ({ isLandingPage = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [closeTimeout, setCloseTimeout] = useState(null);
  const location = useLocation();

  const serviceItems = [
    {
      title: 'Trademark Registration',
      items: [
        { name: 'Register Your Trademark', path: '/services/trademark-registration' },
        { name: 'TTAB Petition', path: '/services/ttab-petition' },
        { name: 'Clearance Search Services', path: '/services/clearance-search' },
        { name: 'Trademark Ownership Transfers', path: '/services/ownership-transfers' },
        { name: 'Managed Trademark Renewal', path: '/services/trademark-renewal' },
        { name: 'Submission of Statements of Use', path: '/services/statements-of-use' },
        { name: 'Support & Response for Trademark Office Actions', path: '/services/office-actions' },
        { name: 'Active Monitoring for Trademark Infringement', path: '/services/infringement-monitoring' },
        { name: 'Support & Guidance for Trademark Cancellations', path: '/services/trademark-cancellations' },
        { name: 'Legal Representation for Trademark Disputes', path: '/services/trademark-disputes' },
        { name: 'Combatting Counterfeiting & Trademark Infringement', path: '/services/anti-counterfeiting' }
      ]
    },
    {
      title: 'Copyright Registration',
      items: [
        { name: 'Register Your Copyright', path: '/services/copyright-registration' },
        { name: 'Copyright Enforcement Assistance', path: '/services/copyright-enforcement' },
        { name: 'Copyright Transfers & Licensing Support', path: '/services/copyright-transfers' },
        { name: 'Legal Representation for Copyright', path: '/services/copyright-legal' },
        { name: 'Copyright Portfolio Management', path: '/services/copyright-portfolio' },
        { name: 'DMCA Compliance', path: '/services/dmca-compliance' }
      ]
    },
    {
      title: 'Patent Services',
      items: [
        { name: 'Existing Patent Search', path: '/services/patent-search' },
        { name: 'Response to Patent Office Actions', path: '/services/patent-office-actions' },
        { name: 'Patent Application Submission', path: '/services/patent-application' },
        { name: 'Legal Representation for Patent Protection', path: '/services/patent-legal' }
      ]
    },
    {
      title: 'Company Formation',
      items: [
        { name: 'LLC', path: '/services/llc-formation' },
        { name: 'C Corporation', path: '/services/c-corporation' },
        { name: 'S Corporation', path: '/services/s-corporation' },
        { name: 'Non-Profit', path: '/services/non-profit' }
      ]
    },
    {
      title: 'Legal Services',
      items: [
        { name: 'Legal Consultation on Partnerships & LLPs', path: '/services/partnership-consultation' },
        { name: 'Due Diligence', path: '/services/due-diligence' },
        { name: 'Business Immigration Law', path: '/services/immigration-law' },
        { name: 'IT & Commercial Law', path: '/services/commercial-law' },
        { name: 'Data Protection & Privacy Law', path: '/services/privacy-law' }
      ]
    },
    {
      title: 'Additional Services',
      items: [
        { name: 'Amazon Brand Registry (US)', path: '/services/amazon-brand-registry' },
        { name: 'Business Formation Services', path: '/services/business-formation' },
        { name: 'Company Dissolution', path: '/services/company-dissolution' },
        { name: 'Issue of Shares', path: '/services/issue-shares' },
        { name: 'Transfer of Shares', path: '/services/transfer-shares' },
        { name: 'Certificate of Good Standing', path: '/services/good-standing' },
        { name: 'Company Name Change', path: '/services/name-change' },
        { name: 'Company Renewals', path: '/services/company-renewals' },
        { name: 'Nominee Director & Secretarial Services', path: '/services/nominee-services' },
        { name: 'Bookkeeping', path: '/services/bookkeeping' },
        { name: 'Tax Advisory & Filing', path: '/services/tax-advisory' },
        { name: 'CFO Services', path: '/services/cfo-services' }
      ]
    }
  ];

  const handleChatClick = () => {
    alert('Chat feature coming soon!');
  };

  if (isLandingPage) {
    return (
      <>
        <nav className="bg-white shadow-md sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Link to="/lp" className="flex items-center">
                {/* <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Trademark Resolutions</h1> */}
                <img src="/assets/img/brand/logo-dark.png" alt="Trademark Resolutions" className="h-12 w-auto" />
              </Link>
              <div className="flex items-center gap-3 sm:gap-4">
                <Button onClick={() => setShowPopup(true)} className="bg-teal-600 hover:bg-teal-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base">
                  Register My Trademark
                </Button>
                <Button onClick={handleChatClick} variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50 px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base">
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </nav>
        {showPopup && <ContactPopup onClose={() => setShowPopup(false)} />}
      </>
    );
  }

  return (
    <>
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img src="/assets/img/brand/logo-dark.png" alt="Trademark Resolutions" className="h-12 w-auto" />
            </Link>
            
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="text-slate-700 hover:text-teal-600 font-medium transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-slate-700 hover:text-teal-600 font-medium transition-colors">
                About Us
              </Link>
              
              {/* Services Mega Menu - Sidebar Style */}
              <div
                className="relative group"
                onMouseEnter={() => {
                  if (closeTimeout) {
                    clearTimeout(closeTimeout);
                    setCloseTimeout(null);
                  }
                  setOpenDropdown('services');
                }}
                onMouseLeave={() => {
                  const timeout = setTimeout(() => {
                    setOpenDropdown(null);
                  }, 100);
                  setCloseTimeout(timeout);
                }}
              >
                <button className="flex items-center gap-1 text-slate-700 hover:text-teal-600 font-medium transition-colors py-2">
                  Services
                  <ChevronDown className="h-4 w-4" />
                </button>
                {(openDropdown === 'services' || openDropdown?.startsWith('services-')) && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 flex overflow-hidden" style={{ minWidth: '820px' }}>
                    {/* Left Sidebar - Main Categories */}
                    <div className="w-72 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 border-r border-gray-200 py-4">
                      {serviceItems.map((category, catIndex) => (
                        <div
                          key={catIndex}
                          onMouseEnter={() => setOpenDropdown(`services-${catIndex}`)}
                          className={`mx-3 px-5 py-3.5 cursor-pointer transition-all duration-300 border-l-4 rounded-lg ${
                            openDropdown === `services-${catIndex}`
                              ? 'bg-gradient-to-r from-teal-600 to-teal-500 border-l-white text-white shadow-lg transform translate-x-1'
                              : 'border-l-transparent hover:bg-white hover:shadow-md text-slate-700 hover:translate-x-0.5'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{category.title}</span>
                            <ChevronRight className={`h-4 w-4 transition-all duration-300 ${
                              openDropdown === `services-${catIndex}` ? 'translate-x-1 scale-110' : ''
                            }`} />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Right Panel - Sub Items */}
                    <div className="w-[530px] h-[420px] flex flex-col bg-white">
                      <div className="flex-1 p-6 overflow-y-auto">
                        {serviceItems.map((category, catIndex) => (
                          openDropdown === `services-${catIndex}` && (
                            <div key={catIndex}>
                              <div className="mb-5 pb-3 border-b-2 border-gradient-to-r from-teal-200 to-teal-100">
                                <h3 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">{category.title}</h3>
                                <p className="text-xs text-gray-500 mt-1.5">Choose from our specialized services</p>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                {category.items.map((item, idx) => (
                                  <Link
                                    key={idx}
                                    to={item.path}
                                    className="group px-4 py-3.5 text-sm text-slate-700 hover:bg-gradient-to-r hover:from-teal-50 hover:to-teal-100 rounded-xl transition-all duration-300 border border-gray-100 hover:border-teal-300 hover:shadow-md"
                                    onClick={() => setOpenDropdown(null)}
                                  >
                                    <div className="flex items-start gap-2.5">
                                      <div className="w-2 h-2 rounded-full bg-teal-400 mt-1.5 group-hover:bg-teal-600 group-hover:scale-125 transition-all duration-300" />
                                      <span className="group-hover:text-teal-600 font-medium leading-tight">{item.name}</span>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )
                        ))}
                        {!openDropdown?.startsWith('services-') && openDropdown === 'services' && (
                          <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-teal-200 rounded-full flex items-center justify-center mb-4 shadow-lg">
                              <ChevronRight className="h-10 w-10 text-teal-600" />
                            </div>
                            <p className="text-gray-600 text-base font-semibold">Hover over a category</p>
                            <p className="text-gray-400 text-sm mt-2">to view available services</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/contact" className="text-slate-700 hover:text-teal-600 font-medium transition-colors">
                Contact Us
              </Link>

              <Button onClick={() => setShowPopup(true)} className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5">
                Get Started
              </Button>
              <Button onClick={handleChatClick} variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50 px-6 py-2.5">
                Chat Now
              </Button>
            </div>

            <button
              className="lg:hidden text-slate-700"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 max-h-96 overflow-y-auto">
            <Link
              to="/"
              className="block px-4 py-3 text-slate-700 hover:bg-gray-50 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block px-4 py-3 text-slate-700 hover:bg-gray-50 font-medium border-t border-gray-100"
              onClick={() => setIsOpen(false)}
            >
              About Us
            </Link>
            
            {/* Services Mobile Menu */}
            <div className="border-t border-gray-100">
              <button
                className="w-full px-4 py-3 text-left font-medium text-slate-700 hover:bg-gray-50 flex items-center justify-between"
                onClick={() => setOpenDropdown(openDropdown === 'services' ? null : 'services')}
              >
                Services
                <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === 'services' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'services' && (
                <div className="bg-gray-50">
                  {serviceItems.map((category, catIndex) => (
                    <div key={catIndex} className="border-b border-gray-200 last:border-0">
                      <div className="px-4 py-2 bg-gray-100 font-semibold text-sm text-slate-900">
                        {category.title}
                      </div>
                      {category.items.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.path}
                          className="block px-6 py-2 text-sm text-slate-600 hover:text-teal-600"
                          onClick={() => {
                            setIsOpen(false);
                            setOpenDropdown(null);
                          }}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="block px-4 py-3 text-slate-700 hover:bg-gray-50 font-medium border-t border-gray-100"
              onClick={() => setIsOpen(false)}
            >
              Contact Us
            </Link>

            <div className="p-4 space-y-2 border-t border-gray-100">
              <Button onClick={() => { setShowPopup(true); setIsOpen(false); }} className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                Get Started
              </Button>
              <Button onClick={handleChatClick} variant="outline" className="w-full border-teal-600 text-teal-600 hover:bg-teal-50">
                Chat Now
              </Button>
            </div>
          </div>
        )}
      </nav>
      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} />}
    </>
  );
};

export default Navbar;